import { Card, CardContent, styled, useTheme } from '@mui/material'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useGetEquipmentByMaintenance } from 'src/api/maintenance'
import { ReactComponent as DeviationAction } from 'src/assets/svg/deviation-action.svg'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { DialogContextType, MaintenanceViewMode } from 'src/enums'
import { IMainComponent } from 'src/generated-types/main-component'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getEquipmentTooltipData, getThemeColor, getTranslatedText } from 'src/utils'
import { formatItemIdentification, getCageDisplayLabel } from 'src/utils/function'
import { DeviationContainerDialog } from '../deviations/DeviationContainerDialog'
import { IDeviationType } from 'src/generated-types/deviation-type'
import GenericInformationTooltip from 'src/components/common/GenericInformationTootip'

export interface ScheduledMaintenanceInformationContainerProps {
  maintenanceId: number
  positionId?: EntityId | undefined
  equipmentTypeId?: EntityId | undefined
  category?: MaintenanceViewMode
  isLocalityMaintenance?: boolean
}

const ScheduledMaintenanceInformationContainer: FC<ScheduledMaintenanceInformationContainerProps> = ({
  maintenanceId,
  positionId,
  equipmentTypeId,
  category,
  isLocalityMaintenance = false,
}) => {
  const theme = useTheme()
  const { customerId, localityId } = useParams()
  const { data: equipment } = useGetEquipmentByMaintenance(
    customerId as EntityId,
    localityId as EntityId,
    maintenanceId,
    positionId,
    equipmentTypeId,
    { enabled: !isLocalityMaintenance },
  )
  const { t } = useTranslation(['main-component', 'common', 'equipment'])
  const { showDialog } = useDialogCtx()

  const showDeviationDialog = useCallback(
    async (mainComponent: IMainComponent, maintenanceId: number) => {
      await showDialog(
        DeviationContainerDialog,
        {
          componentProps: {
            customerId: customerId as EntityId,
            localityId: localityId as EntityId,
            mainComponent: {
              ...mainComponent,
              type: {
                ...mainComponent.type,
                name: mainComponent.mainComponentTypeName,
              },
            },
            maintenanceId: maintenanceId,
            isWithDialog: true,
            deviationTypeFromParent: IDeviationType.Maintenance,
          },
          opts: {
            maxWidth: 'xl',
            fullWidth: true,
          },
        },
        DialogContextType.localityContext,
        true,
      )
    },
    [customerId, localityId],
  )

  const getCardContent = useCallback(
    item => {
      if (category === MaintenanceViewMode.GroupByCage) {
        return formatItemIdentification({
          itemType: getTranslatedText(item.typeName),
          serialNumber: item.serialNumber,
        })
      } else if (category === MaintenanceViewMode.ShowAll) {
        return formatItemIdentification({
          itemType: getTranslatedText(item.typeName),
          positionName: item.positionName,
          positionReference: item.reference,
          positionNote: item.positionNote,
          mainComponentReference: item.mainComponentReference,
          serialNumber: item.serialNumber,
        })
      } else {
        return getCageDisplayLabel(item.positionName, item.reference)
      }
    },
    [category],
  )

  const equipmentWithUniquePositions = useMemo(
    () => Array.from(new Map(equipment?.map(item => [item.positionId, item])).values()),
    [equipment],
  )
  const equipmentArray = useMemo(() => {
    if (category === MaintenanceViewMode.GroupByCage) {
      return equipment?.filter(item => item.positionId === positionId) || []
    } else if (category === MaintenanceViewMode.ShowAll) {
      return equipment || []
    } else {
      return equipmentWithUniquePositions
    }
  }, [equipment, equipmentWithUniquePositions, category, positionId])

  return (
    <>
      <Flex.Row
        sx={{
          paddingLeft: '16px',
          paddingTop: '8px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '15px',
        }}
      >
        {equipmentArray?.map((item, index) => (
          <StyledEquipmentNameCard key={index}>
            <CardContent
              sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: getThemeColor(theme, MUIThemeColors.primaryDark),
                paddingY: '0 !important',
                paddingX: '8px',
                flex: 1,
              }}
            >
              <GenericInformationTooltip data={getEquipmentTooltipData(item as IMainComponent)}>
                {getCardContent(item)}
              </GenericInformationTooltip>
            </CardContent>
            {category !== MaintenanceViewMode.GroupByEquipment && (
              <GridActionButton
                key={'deviation'}
                onClick={() => {
                  showDeviationDialog(item, maintenanceId)
                }}
                variant="outlined"
                shadowWeight={0}
                buttonIcon={<DeviationAction height={18} width={18} />}
                width="23px"
                height="23px"
                toolTipTitle={t('deviation', { ns: 'main-component' })}
              />
            )}
          </StyledEquipmentNameCard>
        ))}
      </Flex.Row>
    </>
  )
}

export default ScheduledMaintenanceInformationContainer

export const StyledEquipmentNameCard = styled(Card)<{ isOverdue?: boolean }>(({ theme, isOverdue }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '32px',
  padding: '6px 12px',
  borderRadius: '4px',
  boxShadow: 'none',
  border: `1px solid ${
    isOverdue ? getThemeColor(theme, MUIThemeColors.errorDark) : getThemeColor(theme, MUIThemeColors.primaryDark)
  }`,
}))
